import { createModel } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import { ChatbotType, PageRequest, PaginationType, ChatbotModelType, RootModel } from '@types';
import { DEFAULT_PAGINATION } from '@commons/constants';
import { Dispatch } from '@redux/store';

const initialState: ChatbotModelType = {
  ids: [],
  loading: false,
  editId: null,
  pagination: DEFAULT_PAGINATION,
  criteria: {
    keyword: '',
  },
  showEditableModal: false,
};

export const chatbots = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    updateCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    updateLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setShowEditableModal: produce((state, payload) => {
      state.showEditableModal = payload;
    }),
  },
  effects: (dispatch: Dispatch) => ({
    async reloadList(payload, state): Promise<void> {
      const { criteria, pagination } = state.chatbots;
      await this.filterAsync({ ...criteria, ...pagination });
    },

    async filterAsync(payload): Promise<void> {
      this.updateLoading(true);
      try {
        let readyPayload = payload;
        if (readyPayload && readyPayload.sort) {
          const clonedPayload = { ...readyPayload };
          const { sort } = clonedPayload;
          if (sort) {
            clonedPayload.sort = sort.map((i) => `${i.property},${i.direction}`).join(',');
          }
          readyPayload = clonedPayload;
        }
        const data: { content: ChatbotType[] } & PaginationType =
          await dispatch.chatbotEntities.load(readyPayload);
        if (data && data.content) {
          this.setIds(data.content.map((scene) => scene.id));
          this.setPagination((({ content, pageable, ...otherProps }) => otherProps)(data));
        }
        this.updateCriteria(payload);
      } catch (e) {
        console.error(e);
        message.error('Failed to filter');
      } finally {
        this.updateLoading(false);
      }
    },

    async pagingAsync(payload: PageRequest, state): Promise<void> {
      const { criteria = {}, pagination = {} } = state.chatbots;
      await this.filterAsync({ ...criteria, ...pagination, ...payload });
    },

    async getAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const item = await dispatch.chatbotEntities.findById(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get ${payload}`);
      } finally {
        this.updateLoading(false);
      }
    },

    async deleteAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        await dispatch.chatbotEntities.delete(payload);
        await this.reloadList();
        message.warning(`Item deleted`);
      } catch (e) {
        console.error(e);
        message.error(`Failed to delete`);
      } finally {
        this.updateLoading(false);
      }
    },

    async saveAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const result = await dispatch.chatbotEntities.save(payload);
        message.success('Save successfully');
        return result;
      } catch (err) {
        console.error(err);
        message.error('Fail to save');
      } finally {
        this.updateLoading(false);
      }
    },
  }),
});
